import { formatDate, t } from "@hakili/i18n";
import { initials } from "@hakili/ui";
import { Plus, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { roleWords } from "../shell/ExpertShell.js";

/**
 * `round C5-2`, `M1`: **« Mon cabinet », the people** , the drawing's
 * `MonCabinet` (« Les personnes », « Inviter un expert », the note under it,
 * the law at the foot). The fiche and « Ma capacité du jeudi » are `M4`'s
 * and their doors'; until then the fiche's lines print the dash a value not
 * there prints (R81).
 *
 * The list is `GET /api/v1/users` , the same door the dealership's team
 * reads, opened to `gererEquipeCabinet` this round , and the invitation is
 * `POST /api/v1/users` with `role: "expertTerrain"`, offered when `/me`
 * answers that role in `mayInviteRoles` and never because of what the screen
 * believes about a responsable. The door's refusal is printed in its words.
 */

export interface TeamMember {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly createdAt: string | null;
}

export interface MonCabinetProps {
  readonly cabinetName: string;
  readonly members: readonly TeamMember[] | null;
  readonly mayInvite: boolean;
  readonly inviter: (input: {
    readonly fullName: string;
    readonly email: string;
    readonly phone: string;
  }) => Promise<{ readonly delivered: boolean }>;
  readonly relire: () => Promise<void>;
  /** `M4`: the fiche and the capacity; `null` until their door answers. */
  readonly card?: {
    readonly accreditation: string | null;
    readonly zoneLabel: string | null;
    readonly since: string | null;
    readonly phone: string | null;
  } | null;
  readonly children?: React.ReactNode;
}

const DASH = t("common.valeur.tiret");

function Ligne({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value ?? DASH}</span>
    </div>
  );
}

export function MonCabinet(props: MonCabinetProps): React.JSX.Element {
  const { cabinetName, members, mayInvite, inviter, relire, card = null, children } = props;
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState<{ tone: "ok" | "ko"; text: string } | null>(null);

  const send = (): void => {
    if (busy) return;
    setBusy(true);
    setWord(null);
    void (async () => {
      try {
        const answer = await inviter({ fullName, email, phone });
        setWord({
          tone: "ok",
          text: answer.delivered
            ? t("expert.cabinet.inviterParti").replace("{email}", email)
            : t("expert.cabinet.inviterNonParti"),
        });
        setOpen(false);
        setFullName("");
        setEmail("");
        setPhone("");
        await relire();
      } catch (failure) {
        setWord({
          tone: "ko",
          text: (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
        });
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="pb-20">
      <div className="px-5 pb-3 pt-5">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.cabinet.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {cabinetName}
          {card?.zoneLabel === undefined || card.zoneLabel === null ? "" : ` · ${card.zoneLabel}`}
        </p>
      </div>

      <div className="mb-4 px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5" data-testid="cabinet-fiche">
          <Ligne label={t("expert.cabinet.agrement")} value={card?.accreditation ?? null} />
          <Ligne label={t("expert.cabinet.zone")} value={card?.zoneLabel ?? null} />
          <Ligne label={t("expert.cabinet.depuis")} value={card?.since ?? null} />
          <Ligne label={t("expert.cabinet.telephone")} value={card?.phone ?? null} />
        </div>
      </div>

      {children}

      <h2 className="mb-2 px-5 text-sm font-semibold">{t("expert.cabinet.personnes")}</h2>
      <div className="mb-4 px-5">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {members === null ? null : members.length === 0 ? (
            <p className="px-4 py-3 text-xs text-gray-400">{t("expert.cabinet.aucunePersonne")}</p>
          ) : (
            <ul className="divide-y divide-gray-50" data-testid="cabinet-personnes">
              {members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-start gap-3 px-4 py-3.5"
                  data-testid="cabinet-personne"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                    {initials(member.fullName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">{member.fullName}</p>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {roleWords(member.role)}
                      </span>
                      {member.status === "invitationEnAttente" ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                          {t("expert.cabinet.invitationEnAttente")}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">{member.email}</p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {member.createdAt === null
                        ? DASH
                        : t("expert.cabinet.personneDepuis").replace(
                            "{date}",
                            formatDate(new Date(member.createdAt)),
                          )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {mayInvite ? (
            <div className="border-t border-gray-100 px-4 py-3">
              {open ? (
                <div className="space-y-2" data-testid="cabinet-inviter-formulaire">
                  <input
                    placeholder={t("expert.cabinet.inviterNom")}
                    value={fullName}
                    onChange={(event) => {
                      setFullName(event.target.value);
                    }}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    data-testid="cabinet-inviter-nom"
                  />
                  <input
                    type="email"
                    placeholder={t("expert.cabinet.inviterEmail")}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    data-testid="cabinet-inviter-email"
                  />
                  <input
                    type="tel"
                    placeholder={t("expert.cabinet.inviterTelephone")}
                    value={phone}
                    onChange={(event) => {
                      setPhone(event.target.value);
                    }}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    data-testid="cabinet-inviter-telephone"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={send}
                      className="flex-1 rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white disabled:opacity-40"
                      data-testid="cabinet-inviter-envoyer"
                    >
                      {t("expert.cabinet.inviterEnvoyer")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                      }}
                      className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600"
                    >
                      {t("expert.cabinet.inviterAnnuler")}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(true);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white"
                  data-testid="cabinet-inviter"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t("expert.cabinet.inviter")}
                </button>
              )}
              {word === null ? null : (
                <p
                  role={word.tone === "ko" ? "alert" : undefined}
                  className={`mt-2 text-xs ${word.tone === "ko" ? "text-rose-600" : "text-emerald-700"}`}
                  data-testid="cabinet-inviter-mot"
                >
                  {word.text}
                </p>
              )}
              <p className="mt-2 text-xs leading-relaxed text-gray-400">{t("expert.cabinet.inviterNote")}</p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-5">
        <div className="flex items-start gap-2.5 rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-gray-600">{t("expert.cabinet.loi")}</p>
        </div>
      </div>
    </div>
  );
}
